import * as Sentry from '@sentry/capacitor'
import { init as sentryVueInit, browserTracingIntegration } from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

// The Capacitor wrapper accepts the underlying Vue SDK options (it forwards
// them via the second-arg `init` function) but its TS surface doesn't model
// that overlap. We type-widen here so `app`, `logErrors`, etc. are accepted.
type SentryInitOptions = Parameters<typeof Sentry.init>[0] & Record<string, unknown>

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const ENVIRONMENT = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined)
  ?? import.meta.env.MODE
const RELEASE = (import.meta.env.VITE_APP_VERSION as string | undefined)
  ?? `matt-mobile@${import.meta.env.MODE}`
const TRACES_SAMPLE_RATE = parseFloat(
  (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined) ?? '0.1'
)

let initialized = false

export function installSentry(app: App, router: Router): void {
  if (!DSN) {
    if (import.meta.env.DEV) {
      console.info('[Sentry] VITE_SENTRY_DSN not set — observability disabled')
    }
    return
  }

  const options: SentryInitOptions = {
    app,
    dsn: DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    tracesSampleRate: TRACES_SAMPLE_RATE,
    // Don't send personally identifying request payloads by default.
    // Headers/cookies are stripped by Sentry server-side, but we also drop
    // the request body for non-error events.
    sendDefaultPii: false,
    integrations: [
      browserTracingIntegration({ router }),
    ],
    // Vue-specific options forwarded via sentryVueInit
    logErrors: import.meta.env.DEV,
    attachProps: true,
    trackComponents: true,
  }

  Sentry.init(options, sentryVueInit)

  initialized = true
}

export function isSentryEnabled(): boolean {
  return initialized
}

export function captureException(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return
  Sentry.captureException(err, context ? { extra: context } : undefined)
}

export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  if (!initialized) return
  Sentry.addBreadcrumb(breadcrumb)
}
